import React from 'react';
import PropTypes from 'prop-types';
import Question from '../../common/Question';
import TextAreaInput from '../../common/TextAreaInput';
import YesNoInputGroup from '../../common/YesNoInputGroup';
import DocumentUpload from '../../DocumentUpload';
import './ExperienceTab.scss';
var isDescEmpty = function isDescEmpty(desc) {
  return !desc || !desc.trim();
};
var taExampleText = "While at Genzyme worked on Endocrine Ph III study and at Cubist\nworked on Ph I Metabolism study.";
var negativeTaExampleText = 'I do not have experience in cardiology but have worked with Class III devices in the pulmonary/respiratory space.';
var diseaseExampleText = 'I designed a Phase II protocol for triple negative breast cancer while at Roche.';
var negativeDiseaseExampleText = "I have not worked with sickle cell anemia specifically but have a strong understanding of blood diseases\nand immune-mediated hematological disorders, as I worked for Celgene.";
var prodRegExampleText = 'I have worked on 10+ IND studies and 4 BLA studies throughout my career across a range of disease areas.';
var negativeProdRegExampleText = 'I have supported clinical trials from phase 2b and 3 as well as post marketing but have not been directly involved with writing INDs.';
var therapiesExampleText = 'I have held several CMC positions for small molecules at Pfizer and J&J.';
var negativeTherapiesExampleText = "I do not have experience in In Vitro Diagnostics using biological samples.\nHowever, I do have experience developing micro-electronics for biotech applications.";
var aboutTextSuggestion = "[Project role] with [#] years of experience working for [small/medium/large/enterprise \nbiotechnology/pharmaceutical/medical device/CRO] companies. With [list your top accomplishments] and expertise in \n[list Therapeutic Area(s) and/or Product Technologies/Modalities needed for the project that match your background], \nI am very interested in this project. Based on my experience, I believe that I would be a terrific fit.";
var renderRequired = function renderRequired(render) {
  if (render) {
    return /*#__PURE__*/React.createElement("div", {
      className: "body-text-2 medium-weight with-error-text mb-2"
    }, /*#__PURE__*/React.createElement("i", {
      className: "fa fa-exclamation-triangle"
    }), ' ', "Required!");
  }
  return null;
};
function ExperienceTab(_ref) {
  var project = _ref.project,
    proposal = _ref.proposal,
    onFormChange = _ref.onFormChange,
    resume = _ref.resume,
    viewResumeUrl = _ref.viewResumeUrl,
    errors = _ref.errors,
    csrfToken = _ref.csrfToken,
    uploadResumeUrl = _ref.uploadResumeUrl,
    userId = _ref.userId;
  var therapeutic_areas = project.therapeutic_areas,
    disease_codes = project.disease_codes,
    regulatory_pathways = project.regulatory_pathways,
    therapies = project.therapies;
  var displayFirstQuestion = therapeutic_areas.length !== 0 || disease_codes.length !== 0 || regulatory_pathways.length !== 0 || therapies.length !== 0;
  var numberIndexes = ['1. ', '2. ', '3. '];
  var letterIndexes = ['a. ', 'b. ', 'c. ', 'd. ', 'e. '];
  var therapeuticAreasError = null;
  if (isDescEmpty(proposal.therapeutic_areas_desc)) {
    therapeuticAreasError = errors.therapeutic_areas_desc;
  }
  var diseaseCodesError = null;
  if (isDescEmpty(proposal.disease_codes_desc)) {
    diseaseCodesError = errors.disease_codes_desc;
  }
  var regulatoryPathwaysError = null;
  if (isDescEmpty(proposal.regulatory_pathways_desc)) {
    regulatoryPathwaysError = errors.regulatory_pathways_desc;
  }
  var therapiesError = null;
  if (isDescEmpty(proposal.therapies_desc)) {
    therapiesError = errors.therapies_desc;
  }
  var aboutError = null;
  if (isDescEmpty(proposal.about_consultant)) {
    aboutError = errors.about_consultant;
  }
  var displayedTaExampleText = taExampleText;
  if (proposal.therapeutic_areas) {
    displayedTaExampleText = proposal.therapeutic_areas.some(function (p) {
      return p.selected === 'No';
    }) ? negativeTaExampleText : taExampleText;
  }
  var displayedDiseaseExampleText = diseaseExampleText;
  if (proposal.disease_codes) {
    displayedDiseaseExampleText = proposal.disease_codes.some(function (p) {
      return p.selected === 'No';
    }) ? negativeDiseaseExampleText : diseaseExampleText;
  }
  var displayedProdRegExampleText = prodRegExampleText;
  if (proposal.regulatory_pathways) {
    displayedProdRegExampleText = proposal.regulatory_pathways.some(function (p) {
      return p.selected === 'No';
    }) ? negativeProdRegExampleText : prodRegExampleText;
  }
  var displayedTherapiesExampleText = therapiesExampleText;
  if (proposal.therapies) {
    displayedTherapiesExampleText = proposal.therapies.some(function (p) {
      return p.selected === 'No';
    }) ? negativeTherapiesExampleText : therapiesExampleText;
  }
  return /*#__PURE__*/React.createElement("div", null, displayFirstQuestion && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "body-text-2 medium-weight mb-5"
  }, numberIndexes.shift(), "Do you have experience in the following ..."), therapeutic_areas.length !== 0 && /*#__PURE__*/React.createElement("div", {
    className: "ms-4 mb-5"
  }, /*#__PURE__*/React.createElement(Question, {
    title: "".concat(letterIndexes.shift(), "Therapeutic Areas"),
    location: "proposal-edit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ms-4"
  }, /*#__PURE__*/React.createElement(YesNoInputGroup, {
    options: therapeutic_areas,
    formKey: "therapeutic_areas",
    value: proposal.therapeutic_areas,
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('therapeutic_areas', val);
    },
    errors: errors
  }), proposal.therapeutic_areas && proposal.therapeutic_areas.some(function (ta) {
    return ta.selected !== null;
  }) && /*#__PURE__*/React.createElement("div", {
    className: "mt-3 therapeutic-areas-description"
  }, renderRequired(therapeuticAreasError), /*#__PURE__*/React.createElement(TextAreaInput, {
    placeholder: "Type your experience here.",
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('therapeutic_areas_desc', val);
    },
    value: proposal.therapeutic_areas_desc,
    error: therapeuticAreasError,
    exampleText: displayedTaExampleText,
    textPromptClass: therapeuticAreasError && 'with-error-text',
    textAreaPrompt: "Tell us specific examples of your experience as it relates to the answers above.",
    required: null
  }))))), disease_codes.length !== 0 && /*#__PURE__*/React.createElement("div", {
    className: "mb-5"
  }, disease_codes.length !== 0 && /*#__PURE__*/React.createElement("div", {
    className: "ms-4 mb-5"
  }, /*#__PURE__*/React.createElement(Question, {
    title: "".concat(letterIndexes.shift(), "Diseases & Indications"),
    location: "proposal-edit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ms-4"
  }, /*#__PURE__*/React.createElement(YesNoInputGroup, {
    options: disease_codes,
    formKey: "disease_codes",
    value: proposal.disease_codes,
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('disease_codes', val);
    },
    errors: errors
  }), proposal.disease_codes && proposal.disease_codes.some(function (dc) {
    return dc.selected !== null;
  }) && /*#__PURE__*/React.createElement("div", {
    className: "disease-codes-description"
  }, renderRequired(diseaseCodesError), /*#__PURE__*/React.createElement(TextAreaInput, {
    placeholder: "Type your experience here.",
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('disease_codes_desc', val);
    },
    value: proposal.disease_codes_desc,
    error: diseaseCodesError,
    exampleText: displayedDiseaseExampleText,
    textPromptClass: diseaseCodesError && 'with-error-text',
    textAreaPrompt: "Tell us specific examples of your experience as it relates to the answers above.",
    required: null
  })))))), regulatory_pathways.length !== 0 && /*#__PURE__*/React.createElement("div", {
    className: "mb-5 ms-5"
  }, /*#__PURE__*/React.createElement(Question, {
    title: "".concat(letterIndexes.shift(), "Regulatory Pathways"),
    location: "proposal-edit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ms-4"
  }, /*#__PURE__*/React.createElement(YesNoInputGroup, {
    options: regulatory_pathways,
    formKey: "regulatory_pathways",
    value: proposal.regulatory_pathways,
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('regulatory_pathways', val);
    },
    errors: errors
  }), proposal.regulatory_pathways && proposal.regulatory_pathways.some(function (rp) {
    return rp.selected !== null;
  }) && /*#__PURE__*/React.createElement("div", {
    className: "regulatory-pathways-description"
  }, renderRequired(regulatoryPathwaysError), /*#__PURE__*/React.createElement(TextAreaInput, {
    placeholder: "Type your experience here.",
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('regulatory_pathways_desc', val);
    },
    value: proposal.regulatory_pathways_desc,
    error: regulatoryPathwaysError,
    exampleText: displayedProdRegExampleText,
    textPromptClass: regulatoryPathwaysError && 'with-error-text',
    textAreaPrompt: "Tell us specific examples of your experience as it relates to the answers above.",
    required: null
  }))))), therapies.length !== 0 && /*#__PURE__*/React.createElement("div", {
    className: "ms-4 mb-5"
  }, /*#__PURE__*/React.createElement(Question, {
    title: "".concat(letterIndexes.shift(), "Product Technologies"),
    location: "proposal-edit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ms-4"
  }, /*#__PURE__*/React.createElement(YesNoInputGroup, {
    options: therapies,
    formKey: "therapies",
    value: proposal.therapies,
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('therapies', val);
    },
    errors: errors
  }), proposal.therapies && proposal.therapies.some(function (t) {
    return t.selected !== null;
  }) && /*#__PURE__*/React.createElement("div", {
    className: "therapies-description"
  }, renderRequired(therapiesError), /*#__PURE__*/React.createElement(TextAreaInput, {
    placeholder: "Type your experience here.",
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('therapies_desc', val);
    },
    value: proposal.therapies_desc,
    error: therapiesError,
    exampleText: displayedTherapiesExampleText,
    textPromptClass: therapiesError && 'with-error-text',
    textAreaPrompt: "Tell us specific examples of your experience as it relates to the answers above.",
    required: null
  })))))), /*#__PURE__*/React.createElement("div", {
    className: "mb-4"
  }, /*#__PURE__*/React.createElement(Question, {
    title: "".concat(numberIndexes.shift(), "Tell employers more about your professional past experience."),
    error: aboutError,
    location: "proposal-edit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ms-4 input-container"
  }, /*#__PURE__*/React.createElement(TextAreaInput, {
    placeholder: "Type your summary here",
    rows: 4,
    onAnswerChange: function onAnswerChange(val) {
      return onFormChange('about_consultant', val);
    },
    value: proposal.about_consultant,
    error: aboutError,
    textPromptClass: aboutError && 'with-error-text',
    className: "proposal-text-area fs-hide proposal-past-experiences",
    textAreaPrompt: "",
    required: null,
    textSuggestion: aboutTextSuggestion
  })))), showResumeQuestion && /*#__PURE__*/React.createElement("div", {
    className: "resume-section mb-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "body-class-2 medium-weight mb-3"
  }, numberIndexes.shift(), "Resume"), /*#__PURE__*/React.createElement("div", {
    className: "ms-4"
  }, /*#__PURE__*/React.createElement(DocumentUpload, {
    buttonStyle: "button",
    prompt: "Upload Resume",
    viewDocumentUrl: viewResumeUrl,
    uploadDocumentUrl: uploadResumeUrl,
    userId: userId,
    documentType: "resume",
    location: "proposal-edit",
    csrfToken: csrfToken,
    document: resume,
    proposalResourceId: proposal.resource_id,
    saveOnUpload: true
  }))));
}
ExperienceTab.propTypes = {
  project: PropTypes.shape({}).isRequired,
  proposal: PropTypes.shape({}).isRequired,
  onFormChange: PropTypes.func.isRequired,
  showResumeQuestion: PropTypes.bool,
  resume: PropTypes.shape({}),
  viewResumeUrl: PropTypes.string.isRequired,
  uploadResumeUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  errors: PropTypes.shape({}).isRequired,
  userId: PropTypes.string
};
ExperienceTab.defaultProps = {
  showResumeQuestion: true,
  resume: null,
  userId: ''
};
export default ExperienceTab;